/**
 * ClosedProposalsSelector — selezione proposte chiuse + pagamenti (Fase 33.1)
 * con collegamento Codice Immobile → auto-sold (Fase 53).
 * ★ Fase 59.P2 — estratto da StageTransitionModal.jsx. Comportamento invariato.
 */
import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import i18n from '../../i18n';

export function ClosedProposalsSelector({
  sentProposals, loadingSent, closedPayments, setPay, availableUnits,
}) {
  if (loadingSent) {
    return (
      <div className="rounded-md border border-border p-3 text-xs text-muted-foreground">
        {i18n.t('closedSel.loading', 'Caricamento proposte inviate…')}
      </div>
    );
  }
  if (!sentProposals || sentProposals.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
        {i18n.t('closedSel.none', 'Nessuna proposta risulta essere stata inviata a questo lead — puoi comunque procedere.')}
      </div>
    );
  }
  const totalSelected = sentProposals
    .filter((sp) => closedPayments[sp.proposal_id]?.checked)
    .reduce((acc, sp) => acc + (parseFloat(closedPayments[sp.proposal_id]?.amount) || 0), 0);
  return (
    <div className="space-y-3" data-testid="closed-proposals-selector">
      <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900">
        {i18n.t('closedSel.intro', 'Seleziona quali proposte sono state effettivamente chiuse. Per ognuna verrà creato un record Pagamento visibile nella pagina Pagamenti.')}
      </div>
      {sentProposals.map((sp) => {
        const st = closedPayments[sp.proposal_id] || {};
        return (
          <div
            key={sp.proposal_id}
            className={`border rounded-md p-3 space-y-2 ${st.checked ? 'bg-emerald-50/60 border-emerald-300' : 'bg-slate-50/50 border-border'}`}
            data-testid={`closed-prop-item-${sp.proposal_id}`}
          >
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!st.checked}
                onChange={(e) => setPay(sp.proposal_id, 'checked', e.target.checked)}
                className="rounded"
                data-testid={`closed-prop-check-${sp.proposal_id}`}
              />
              <div className="flex-1">
                <div className="text-sm font-medium">{sp.name}</div>
                <div className="text-[11px] text-muted-foreground">
                  {sp.country} · {sp.path} · {i18n.t('closedSel.listPrice', 'prezzo listino')} €{(sp.price_eur || 0).toLocaleString('it-IT')}
                </div>
              </div>
            </label>
            {st.checked && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-6">
                {/* ★ 2026-08-14 (Fase 53) — Codice Immobile: al primo pagamento
                    l'unità selezionata passa automaticamente a "sold" nel DB
                    e sparisce dalla landing pubblica. */}
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase text-[var(--gp-gold)] font-bold">
                    🏠 {i18n.t('closedSel.unitCode', 'Codice immobile (chiude l\'unità automaticamente)')}
                  </Label>
                  <select
                    value={st.unit_id || ''}
                    onChange={(e) => {
                      const uid = e.target.value;
                      const u = availableUnits.find((x) => x.id === uid);
                      setPay(sp.proposal_id, 'unit_id', uid);
                      setPay(sp.proposal_id, 'unit_code', u?.code || '');
                      setPay(sp.proposal_id, 'unit_project_slug', u?.project_slug || '');
                      // Pre-fill amount se non ancora inserito
                      if (u?.price_total && (!st.amount || parseFloat(st.amount) === 0)) {
                        setPay(sp.proposal_id, 'amount', String(u.price_total));
                      }
                    }}
                    className="mt-1 w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid={`closed-prop-unit-${sp.proposal_id}`}
                  >
                    <option value="">{i18n.t('closedSel.noUnit', '— Nessun immobile collegato (opzionale) —')}</option>
                    {(() => {
                      // Filtra per country se possibile (fuzzy)
                      const country = (sp.country || '').toUpperCase();
                      const slugPrefs = [];
                      if (country.includes('ALBAN')) slugPrefs.push('la-mar-residence', 'narea', 'palm-paradise', 'vista-mare');
                      if (country.includes('INDON')) slugPrefs.push('green-paradise-lombok', 'lombok');
                      if (country.includes('THAI'))  slugPrefs.push('chumphon');
                      if (country.includes('NORV') || country.includes('NORD')) slugPrefs.push('spille');
                      const preferred = slugPrefs.length > 0
                        ? availableUnits.filter((u) => slugPrefs.includes(u.project_slug))
                        : availableUnits;
                      const rest = slugPrefs.length > 0
                        ? availableUnits.filter((u) => !slugPrefs.includes(u.project_slug))
                        : [];
                      return (
                        <>
                          {preferred.length > 0 && (
                            <optgroup label={`${i18n.t('closedSel.suggested', 'Suggeriti')} (${sp.country || '—'})`}>
                              {preferred.map((u) => (
                                <option key={u.id} value={u.id}>{u.label}</option>
                              ))}
                            </optgroup>
                          )}
                          {rest.length > 0 && (
                            <optgroup label={i18n.t('closedSel.otherProjects', 'Altri progetti')}>
                              {rest.map((u) => (
                                <option key={u.id} value={u.id}>{u.label}</option>
                              ))}
                            </optgroup>
                          )}
                        </>
                      );
                    })()}
                  </select>
                  <div className="text-[10px] text-muted-foreground mt-1 italic">
                    {st.unit_code
                      ? <span className="text-emerald-700 font-medium">✓ {i18n.t('closedSel.unitWillBeSoldPre', 'Immobile')} <b>{st.unit_code}</b> {i18n.t('closedSel.unitWillBeSoldPost', 'verrà marcato come venduto al salvataggio.')}</span>
                      : <>{i18n.t('closedSel.unitOptional', 'Facoltativo. Se selezioni un codice, verrà chiuso automaticamente e rimosso dalla disponibilità pubblica.')}</>}
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase">{i18n.t('closedSel.closedAmount', 'Importo chiuso (EUR)')} *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={st.amount || ''}
                    onChange={(e) => setPay(sp.proposal_id, 'amount', e.target.value)}
                    className="mt-1 h-9"
                    data-testid={`closed-prop-amount-${sp.proposal_id}`}
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase">{i18n.t('closedSel.payDate', 'Data pagamento')} *</Label>
                  <Input
                    type="date"
                    value={st.paid_at || ''}
                    onChange={(e) => setPay(sp.proposal_id, 'paid_at', e.target.value)}
                    className="mt-1 h-9"
                    data-testid={`closed-prop-date-${sp.proposal_id}`}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase">{i18n.t('closedSel.payMethod', 'Metodo di pagamento')} *</Label>
                  <Textarea
                    rows={2}
                    value={st.method || ''}
                    onChange={(e) => setPay(sp.proposal_id, 'method', e.target.value)}
                    placeholder={i18n.t('closedSel.payMethodPh', 'Es. Bonifico 50% + 3 rate a 30/60/90 gg — IBAN IT...')}
                    className="mt-1"
                    data-testid={`closed-prop-method-${sp.proposal_id}`}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase">{i18n.t('closedSel.notes', 'Note')}</Label>
                  <Input
                    value={st.notes || ''}
                    onChange={(e) => setPay(sp.proposal_id, 'notes', e.target.value)}
                    placeholder={i18n.t('closedSel.optional', 'Facoltativo')}
                    className="mt-1 h-9"
                    data-testid={`closed-prop-notes-${sp.proposal_id}`}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
      {totalSelected > 0 && (
        <div className="text-right text-sm font-semibold text-emerald-800" data-testid="closed-prop-total">
          {i18n.t('closedSel.totalPayments', 'Totale pagamenti:')} €{totalSelected.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      )}
    </div>
  );
}
